import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link2, ListFilter, Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import type { MasterSection } from './Admin'
import { EntryDialog, type MasterEntry } from './EntryDialog'
import { Swatch } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Field'
import { Card, LoadingPanel } from '@/components/ui/misc'
import { api, queryString } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'

interface ListResponse {
  entries: MasterEntry[]
  total: number
  activeCount: number
  inactiveCount: number
  allCount: number
}

const PAGE_SIZE = 10

export function MasterDataTable({ section }: { section: MasterSection }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<MasterEntry | null>(null)
  const [creating, setCreating] = useState(false)
  const [deactivating, setDeactivating] = useState<MasterEntry | null>(null)

  const listKey = ['admin', 'master-data', section.kind, { search, status, page }] as const

  const { data, isLoading } = useQuery({
    queryKey: listKey,
    queryFn: () =>
      api.get<ListResponse>(
        `/admin/master-data/${section.kind}${queryString({ search, status, page, pageSize: PAGE_SIZE })}`,
      ),
  })

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin', 'master-data'] }),
      queryClient.invalidateQueries({ queryKey: ['master-data'] }),
    ])

  const toggleStatus = useMutation({
    mutationFn: ({ id, nextStatus }: { id: string; nextStatus: 'active' | 'inactive' }) =>
      api.post(`/admin/master-data/${section.kind}/${id}/status`, { status: nextStatus }),
    onSuccess: async (_result, variables) => {
      await invalidate()
      setDeactivating(null)
      toast.success(variables.nextStatus === 'active' ? 'Entry activated.' : 'Entry deactivated.')
    },
    onError: () => toast.error('Could not change that entry.'),
  })

  const entries = data?.entries ?? []
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))
  const isDyeSource = section.kind === 'dyeSource'

  return (
    <>
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-ink">{section.label}</h2>
          <p className="mt-0.5 text-[13px] text-ink-muted">
            {data ? `${data.allCount} entries · ${data.activeCount} active · ${data.inactiveCount} inactive` : '—'}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" />
          Add entry
        </Button>
      </header>

      <div className="mb-4 flex items-center gap-3">
        <div className="flex-1">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Search name, scientific name or ID..."
            icon={<Search className="size-4" />}
          />
        </div>
        <div className="flex items-center gap-2 text-[12px] text-ink-muted">
          <ListFilter className="size-3.5" />
          Admin status
        </div>
        <div className="flex rounded-[var(--radius-control)] border border-line p-0.5">
          {(
            [
              ['all', `All (${data?.allCount ?? 0})`],
              ['active', `Active (${data?.activeCount ?? 0})`],
              ['inactive', `Inactive (${data?.inactiveCount ?? 0})`],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setStatus(value)
                setPage(1)
              }}
              className={cn(
                'rounded-[6px] px-3 py-1.5 text-[12px] font-medium transition-colors',
                status === value ? 'bg-brand-soft text-brand' : 'text-ink-muted hover:text-ink',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <LoadingPanel label="Loading entries" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-paper-muted/60 text-left text-[12px] text-ink-muted">
                <th className="px-4 py-2.5 font-semibold">Entry ID</th>
                <th className="px-4 py-2.5 font-semibold">Canonical name</th>
                {isDyeSource && <th className="px-4 py-2.5 font-semibold">Scientific name</th>}
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Linked records</th>
                <th className="px-4 py-2.5 font-semibold">Updated</th>
                <th className="px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className={cn(
                    'border-b border-line last:border-0',
                    entry.status === 'inactive' && 'bg-paper-muted/50',
                  )}
                >
                  <td className="px-4 py-3 font-medium text-brand">{entry.code}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2">
                      {isDyeSource && <Swatch hex={entry.swatchHex} size="xs" className="rounded-full" />}
                      <span className="text-ink">{entry.name}</span>
                    </span>
                  </td>
                  {isDyeSource && (
                    <td className="px-4 py-3 italic text-ink-muted">{entry.scientificName}</td>
                  )}
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 text-[12px]',
                        entry.status === 'active' ? 'text-positive' : 'text-ink-muted',
                      )}
                    >
                      <span
                        className={cn(
                          'size-1.5 rounded-full',
                          entry.status === 'active' ? 'bg-positive' : 'bg-ink-faint',
                        )}
                      />
                      {entry.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-brand">
                      <Link2 className="size-3.5" />
                      {entry.linkedRecords} records
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-muted">{formatDate(entry.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setEditing(entry)}
                      className="mr-3 font-semibold text-ink-soft hover:text-ink"
                    >
                      Edit
                    </button>
                    {entry.status === 'active' ? (
                      <button
                        type="button"
                        onClick={() => setDeactivating(entry)}
                        className="font-semibold text-brand hover:underline"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleStatus.mutate({ id: entry.id, nextStatus: 'active' })}
                        className="font-semibold text-positive hover:underline"
                      >
                        Activate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-ink-muted">
                    No entries match those filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-[12px] text-ink-muted">
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          <span>
            Showing {entries.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}-
            {(page - 1) * PAGE_SIZE + entries.length} of {data?.total ?? 0} entries
          </span>
        </div>
      </Card>

      <EntryDialog
        kind={section.kind}
        label={section.label}
        open={creating || Boolean(editing)}
        entry={editing}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false)
            setEditing(null)
          }
        }}
        onSaved={invalidate}
      />

      <Dialog
        open={Boolean(deactivating)}
        onOpenChange={(open) => !open && setDeactivating(null)}
        size="sm"
        title="Deactivate this entry?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeactivating(null)}>
              Cancel
            </Button>
            <Button
              loading={toggleStatus.isPending}
              onClick={() =>
                deactivating && toggleStatus.mutate({ id: deactivating.id, nextStatus: 'inactive' })
              }
            >
              Deactivate
            </Button>
          </>
        }
      >
        {deactivating && (
          <div className="space-y-4 text-[13px] text-ink-soft">
            <div className="rounded-[var(--radius-control)] border border-line bg-paper-muted p-3">
              <p className="flex items-center justify-between">
                <span className="font-display text-base font-semibold text-ink">
                  {deactivating.name}
                </span>
                <span className="inline-flex items-center gap-1.5 text-[12px] text-positive">
                  <span className="size-1.5 rounded-full bg-positive" />
                  Active
                </span>
              </p>
              <p className="mt-0.5 text-[12px] text-ink-muted">
                {section.label} · {deactivating.code} · {deactivating.linkedRecords} linked records
              </p>
            </div>

            <p className="flex gap-2">
              <Link2 className="mt-0.5 size-4 shrink-0 text-ink-faint" />
              All {deactivating.linkedRecords} linked records remain intact and continue to display
              &ldquo;{deactivating.name}&rdquo;.
            </p>
            <p className="flex gap-2">
              <ListFilter className="mt-0.5 size-4 shrink-0 text-ink-faint" />
              The entry disappears from researcher filter menus and new selection lists.
            </p>
            <p className="text-ink-muted">
              It stays visible in Admin / Master data and can be activated again at any time.
            </p>
            <p className="text-ink-muted">
              This changes availability, not the records already linked to this entry.
            </p>
          </div>
        )}
      </Dialog>
    </>
  )
}

export function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number
  totalPages: number
  onChange: (page: number) => void
}) {
  if (totalPages <= 1) return <span />

  const pages = Array.from({ length: Math.min(3, totalPages) }, (_unused, index) => index + 1)

  return (
    <div className="flex items-center gap-1">
      <PageButton disabled={page === 1} onClick={() => onChange(page - 1)}>
        ‹
      </PageButton>
      {pages.map((value) => (
        <PageButton key={value} active={value === page} onClick={() => onChange(value)}>
          {value}
        </PageButton>
      ))}
      {totalPages > 3 && <span className="px-1 text-ink-faint">…</span>}
      <PageButton disabled={page === totalPages} onClick={() => onChange(page + 1)}>
        ›
      </PageButton>
    </div>
  )
}

function PageButton({
  children,
  active,
  disabled,
  onClick,
}: {
  children: React.ReactNode
  active?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-7 items-center justify-center rounded-md border text-[12px] transition-colors',
        active
          ? 'border-brand bg-brand text-white'
          : 'border-line text-ink-soft hover:bg-paper-muted disabled:opacity-40',
      )}
    >
      {children}
    </button>
  )
}
